import {
  Button,
  createStyles,
  Drawer,
  FormControl,
  FormLabel,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Theme,
} from '@material-ui/core';
import {DatePicker} from '@material-ui/pickers';
import React, {useEffect, useState} from 'react';
import * as Luxon from 'luxon';
import './FilterComponent.sass';
import {pipe} from 'fp-ts/lib/pipeable';
import {Autocomplete} from '@material-ui/lab';
import {toEither} from 'Core/models/Async';
import {fold as foldEither} from 'fp-ts/lib/Either';
import {renderAsync} from '../RenderAsync';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {
  getAccountNumbers,
  getProducts,
  getProcessingAccuracyChartDetails,
  changeProcessAccuracyRecords,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {createSnackbar} from 'Core/ducks/snackbar';
import {AutoCompleteList} from 'Enrollment/models/AutoCompleteDto';
import Loading from '../Loading';

type FilterComponentProps = {
  open: boolean;
  onClose: () => void;
};
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > * + *': {
        marginTop: theme.spacing(3),
      },
    },
  })
);

const FilterComponent = (props: FilterComponentProps) => {
  const classes = useStyles();

  const {accountNumbers, products, isLoadData} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<AutoCompleteList[] | null>();
  const [selectedProductType, setSelectedProductType] = useState('');
  const [getStartDate, setStartDate] = useState('');
  const [getEndDate, setEndDate] = useState('');

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getAccountNumbers());
    dispatch(getProducts());
    setSelectedProductType('ALL');
  }, []);

  useEffect(() => {
    if (isLoadData) {
      props.onClose();
    }
  }, [isLoadData]);

  const handleSearchApply = () => {
    let newStartDate = new Date(getStartDate);
    const startDateFilter = newStartDate
      ? Luxon.DateTime.utc(newStartDate.getFullYear(), newStartDate.getMonth() + 1, newStartDate.getDate())
          .startOf('day')
          .toISO()
      : '';

    let newEndDate = new Date(getEndDate);

    const endDateFilter = newEndDate
      ? Luxon.DateTime.utc(newEndDate.getFullYear(), newEndDate.getMonth() + 1, newEndDate.getDate())
          .endOf('day')
          .toISO()
      : '';

    const accountNumbers = selectedAccountNumber?.map(x => x.id).join();
    dispatch(changeProcessAccuracyRecords(false));
    dispatch(
      getProcessingAccuracyChartDetails(
        undefined,
        undefined,
        undefined,
        undefined,
        accountNumbers,
        selectedProductType,
        startDateFilter,
        endDateFilter
      )
    );
  };
  return (
    <div>
      <Drawer open={props.open} onClose={props.onClose} variant="temporary" anchor="right">
        <div className="column-drawer column-drawer__ET-dashboardFilters">
          <FormControl component="fieldset">
            <FormLabel className="column-drawer__title">Filter Criteria</FormLabel>
            <div className="product-filter">
              {pipe(
                toEither(products),
                foldEither(renderAsync, productType => (
                  <Select
                    className="enrollment-dashboard__filters--selectBox"
                    id="enrollment-dashboard-products-filter"
                    value={selectedProductType}
                    onChange={e => setSelectedProductType(e.target.value as string)}
                  >
                    {productType.sort().map((type, index) => (
                      <MenuItem key={index} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </Select>
                ))
              )}
            </div>
            <div className="accountNumber-filter">
              <div className={classes.root}>
                {pipe(
                  toEither(accountNumbers),
                  foldEither(renderAsync, accountNumbers => (
                    <Autocomplete
                      className="enrollment-dashboard__filters--employers"
                      multiple
                      id="enrollment-dashboard-employers-filter"
                      options={accountNumbers}
                      limitTags={2}
                      getOptionLabel={option => option.id!}
                      value={selectedAccountNumber!}
                      onChange={(e, newValue) => setSelectedAccountNumber(newValue)}
                      openOnFocus
                      renderInput={params => <TextField {...params} label="Account Number" variant="outlined" />}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="date-filter">
              <DatePicker
                clearable
                label="Start Date"
                format="MMMM d, yyyy"
                className="date-filter__input"
                value={getStartDate || new Date(((new Date().getFullYear() as number) - 1), new Date().getMonth(), 1)}
                shouldDisableDate={date => {
                  return !getEndDate || !date ? false : date > Luxon.DateTime.fromJSDate(new Date(getEndDate));
                }}
                onChange={date => setStartDate(date ? (date as Luxon.DateTime).startOf('day').toISO() : '')}
              />
            </div>

            <div className="date-filter">
              <DatePicker
                clearable
                label="End Date"
                className="date-filter__input"
                format="MMMM d, yyyy"
                shouldDisableDate={date => {
                  return !getStartDate || !date ? false : date < Luxon.DateTime.fromJSDate(new Date(getStartDate));
                }}
                value={getEndDate || new Date()}
                onChange={date => setEndDate(date ? (date as Luxon.DateTime).endOf('day').toISO() : '')}
              />
            </div>
          </FormControl>

          <div className="action-buttons">
            <Button
              id={`visible-columns-drawer-preference-button`}
              variant="contained"
              color="primary"
              className="btn-apply"
              disabled={!isLoadData}
              onClick={() => handleSearchApply()}
            >
              Apply
            </Button>
            <div>{!isLoadData ? <Loading /> : ''}</div>
            <Button id={`visible-columns-drawer-preference-button`} variant="contained" onClick={props.onClose}>
              Close
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
};

export default FilterComponent;
